import { useMemo } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { ProcurementPlanRetailer } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Flex,
  type HTMLChakraProps,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  RadioGroupItem as RawRadioGroupItem,
  RadioGroupItemText as RawRadioGroupItemText,
  Select,
  type SelectOption,
} from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import {
  DateStepper,
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters'
import { selectFontFix } from '~/components/common/filters/selectFontFix'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { formatDateDisplay, isSameDay } from '~/models/dateTime'
import type { DisplayMode } from '~/pages/ProcurementPlanDetail/models/displayMode'

// chakra v3.29 + React 18 型定義の既知不具合 (DemandForecastWorkspace と同じ
// workaround) で RadioGroupItem* の children / value が型上欠落するため cast する。
const RadioGroupItem = RawRadioGroupItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; disabled?: boolean }
>
const RadioGroupItemText = RawRadioGroupItemText as React.FC<
  HTMLChakraProps<'span'>
>

function toAreaOption(code: AreaCode): SelectOption {
  return { value: String(code), label: convertAreaLabel(code) }
}

/** BG（全体）選択を表す値。business_master_id とは名前空間が別 (design §5 種別 state)。 */
export const BG_ALL_SELECTION = 'bg'

type DetailFilterBarCommonProps = {
  date: Date
  area: AreaCode
  /**
   * 配下小売電気事業者の内訳一覧 (retailer_breakdowns)。空配列なら roster 無し
   * (生成経路・旧取込分、procurement-plan-retailer-breakdown design AC-07) を
   * 意味し、小売切替を disabled にして BG（全体）のみ選択可能にする。
   */
  retailers: ProcurementPlanRetailer[]
  /** 'bg' (= BG 全体) または retailer_code。 */
  selectedRetailer: string
  /**
   * ワークフローケース由来で受渡日が固定されているか (issue #7131 /
   * AC-WFW-006a Decision 4)。 true のとき対象日 DateStepper のみ disabled にする
   * (表示日・エリア絞り込みは計画 record のものをそのまま表示し続け、強制
   * navigate はしない)。
   */
  isDateLocked?: boolean
  /**
   * ワークフローケースの対象日 (取得完了後のみ確定。 未取得 / ケース無しの間は
   * undefined)。 **表示中の `date` がこの日と一致する時だけ** Badge
   * 「ケース対象日に固定」 を出す。
   *
   * 本画面は Decision 4 により強制 navigate をせず、 表示日は計画 record 由来
   * (`filterOverride ?? initialFilter ?? serverNow()`) なので、 ケース対象日と
   * **一致しないことがある**。 以前は「ケース対象日を取得できたか」 だけを
   * boolean で受け取っていたため、 固定されていない日付に「固定」 と表示して
   * いた (#7173)。 2 つの日付を同じ場所で突き合わせられるよう Date で受ける。
   */
  caseTargetDate?: Date
}

/** 版の詳細: 対象日・エリア・種別・表示モードを操作できる。 */
type EditableProps = {
  readOnly?: false
  /**
   * エリアの選択肢 (AC-PLC-019: OCCTO 計画提出の対象 10 エリア ∩ 業務エリア設定、
   * conventions.md の順)。呼び出し元が `useBusinessAreaFilter` で絞って渡す。
   */
  selectableAreaCodes: readonly AreaCode[]
  onDateChange: (date: Date) => void
  onAreaChange: (area: AreaCode) => void
  onRetailerChange: (selection: string) => void
  /** 表示モード（取引先毎合計 / 取引毎、design `#sd-view-seg`）。 */
  displayMode: DisplayMode
  onDisplayModeChange: (mode: DisplayMode) => void
}

/**
 * eBalance に提出記録の無い計画の詳細 (AC-PPD-028、issue #10266): 対象日・エリア・
 * 種別は表示するだけで操作させず、表示モードの切替は出さない (表示は取引先毎合計だけ)。
 * 版の ID が無いので日付・エリアを変えた時の行き先が定まらない。
 */
type ReadOnlyProps = {
  readOnly: true
}

type DetailFilterBarProps = DetailFilterBarCommonProps &
  (EditableProps | ReadOnlyProps)

/**
 * 需要調達計画詳細画面のフィルタバー（エリア → 種別 → 表示 → 対象日、
 * design `plan/submission-detail.html` `#sd-filter-bar` の `?type=procurement`
 * 表示順に一致。procurement-plan-detail-filter 設計書）。種別 (BG/小売) 切替と
 * 表示モード切替はどちらもページ内 state のみで API 呼び出しを伴わない
 * (設計書 §5)。
 */
export const DetailFilterBar = (
  props: DetailFilterBarProps,
): React.ReactElement => {
  const {
    date,
    area,
    retailers,
    selectedRetailer,
    isDateLocked = false,
    caseTargetDate,
  } = props
  const editable = props.readOnly === true ? undefined : props
  const displayMode: DisplayMode =
    editable?.displayMode ?? 'perCounterpartyTotal'
  const { t } = useTranslation()
  const { dateLockedReason } = useWorkflowLockReasons()

  // 読み取り専用 (AC-PPD-028) は選択肢を開けないので、絞り込まずに対象エリア全件を使う。
  const areaCodes = editable?.selectableAreaCodes ?? AREA_CODE_OPTIONS
  const areaSelectOptions: SelectOption[] = useMemo(
    () => areaCodes.map(toAreaOption),
    [areaCodes],
  )
  // 選択中の値は選択肢から引かずに表示中のエリアから組む。業務エリアの外の計画を
  // URL や提出管理のリンクで開いた時も (conventions.md: 直接アクセスは通常どおり表示)、
  // 選択肢に足さずに表示中のエリア名を出すため。
  const selectedAreaOption =
    area === AreaCode.UNSPECIFIED ? null : toAreaOption(area)

  // AC-07: roster 空 (生成経路・旧取込分) は小売切替を disabled にし、
  // BG（全体）のみ選択可能にする。
  const retailerSwitchDisabled = retailers.length === 0

  const retailerLabel = (retailer: ProcurementPlanRetailer): string =>
    // 直前の事業者名と隣接して描画されるため、かっこの前の
    // 半角スペースはこの文字列側に持つ (conventions.md「かっこ」)。
    `${retailer.retailerName ?? retailer.retailerCode}${
      retailer.isOwn
        ? ` (${t('page:procurementPlanDetail.filter.retailerIsOwn')})`
        : ''
    }`
  const selectedRetailerItem = retailers.find(
    (retailer) => retailer.retailerCode === selectedRetailer,
  )

  /*
   * 折りたたんでも見える要約 (AC-PPD-008-1: 対象日・エリア・種別・表示モードをチップで 1 行)。
   * 並びは design `plan/submission-detail.html` `renderFilterChips` の 日付 → 種別 → 表示 → エリア
   * (spec は項目だけを定め、順序は定めていない)。
   */
  const summaryChips = [
    { key: 'date', label: formatDateDisplay(date) },
    {
      key: 'retailer',
      label:
        selectedRetailerItem === undefined
          ? t('page:procurementPlanDetail.filter.bgAll')
          : retailerLabel(selectedRetailerItem),
    },
    {
      key: 'displayMode',
      label:
        displayMode === 'perTransaction'
          ? t('page:procurementPlanDetail.filter.displayMode.perTransaction')
          : t(
              'page:procurementPlanDetail.filter.displayMode.perCounterpartyTotal',
            ),
    },
    { key: 'area', label: convertAreaLabel(area) },
  ]
  const summary = (
    <Flex flexWrap="wrap" flex={1} gap={1.5} minWidth={0}>
      {summaryChips.map((chip) => (
        <Badge key={chip.key} status="neutral">
          {chip.label}
        </Badge>
      ))}
    </Flex>
  )

  return (
    <FilterToolbar labelVariant="formLabel" summary={summary}>
      <FilterSection label={t('page:procurementPlanDetail.filter.area')}>
        {/* 幅は外側の Box (design style="width:140px" 準拠)。 DS Select に
            chakraStyles を渡すと DS 側の chakraStyles が丸ごと置き換わり、
            選択時背景・list の font-size・▽ 左の区切り線が失われる。 */}
        <Box minWidth="160px" css={selectFontFix}>
          <Select
            aria-label={t('page:procurementPlanDetail.filter.area')}
            isDisabled={editable === undefined}
            isMulti={false}
            // menu を body へ portal する。 直下の 48 コマテーブルの sticky thead
            // (zIndex 2/3) が inline の menu を覆うため、 テーブルの重なり文脈から
            // 脱出させる (`~/components/common/filters/AreaFilter.tsx` と同じ扱い)。
            menuPortalTarget={document.body}
            onChange={(opt) => {
              if (opt) {
                editable?.onAreaChange(Number(opt.value) as AreaCode)
              }
            }}
            options={areaSelectOptions}
            selectedOptions={selectedAreaOption}
            size="md"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 1500 }) }}
          />
        </Box>
      </FilterSection>

      <FilterSection label={t('page:procurementPlanDetail.filter.retailer')}>
        <RadioGroupRoot
          onValueChange={(d: { value: string | null }) => {
            if (d.value !== null) {
              editable?.onRetailerChange(d.value)
            }
          }}
          value={selectedRetailer}
        >
          <Flex alignItems="center" flexWrap="wrap" gap={4}>
            <RadioGroupItem
              disabled={retailerSwitchDisabled}
              value={BG_ALL_SELECTION}
            >
              <RadioGroupItemHiddenInput />
              <RadioGroupItemControl />
              <RadioGroupItemText fontSize="md">
                {t('page:procurementPlanDetail.filter.bgAll')}
              </RadioGroupItemText>
            </RadioGroupItem>
            {retailers.map((retailer) => (
              <RadioGroupItem
                key={retailer.retailerCode}
                value={retailer.retailerCode}
              >
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText fontSize="md">
                  {/* 画面状態マトリクス: 自社事業者 (is_own) には自社ラベルを
                      併記する (procurement-plan-retailer-breakdown design、
                      現行 RetailerInfo.is_own 表示の代替)。 */}
                  {retailerLabel(retailer)}
                </RadioGroupItemText>
              </RadioGroupItem>
            ))}
          </Flex>
        </RadioGroupRoot>
      </FilterSection>

      {editable !== undefined && (
        <FilterSection
          label={t('page:procurementPlanDetail.filter.displayMode.label')}
        >
          <RadioGroupRoot
            onValueChange={(d: { value: string | null }) => {
              if (
                d.value === 'perTransaction' ||
                d.value === 'perCounterpartyTotal'
              ) {
                editable.onDisplayModeChange(d.value)
              }
            }}
            value={displayMode}
          >
            <Flex alignItems="center" flexWrap="wrap" gap={4}>
              {/* design #sd-view-seg: 取引先毎合計 が先 (既定 checked) */}
              <RadioGroupItem value="perCounterpartyTotal">
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText fontSize="md">
                  {t(
                    'page:procurementPlanDetail.filter.displayMode.perCounterpartyTotal',
                  )}
                </RadioGroupItemText>
              </RadioGroupItem>
              <RadioGroupItem value="perTransaction">
                <RadioGroupItemHiddenInput />
                <RadioGroupItemControl />
                <RadioGroupItemText fontSize="md">
                  {t(
                    'page:procurementPlanDetail.filter.displayMode.perTransaction',
                  )}
                </RadioGroupItemText>
              </RadioGroupItem>
            </Flex>
          </RadioGroupRoot>
        </FilterSection>
      )}

      <FilterSection label={t('page:procurementPlanDetail.filter.targetDate')}>
        <DateStepper
          dateAriaLabel={t('page:procurementPlanDetail.filter.targetDate')}
          disabled={isDateLocked || editable === undefined}
          disabledReason={isDateLocked ? dateLockedReason : undefined}
          onChange={(next) => {
            editable?.onDateChange(next)
          }}
          value={date}
        />
        {caseTargetDate !== undefined && isSameDay(date, caseTargetDate) && (
          <Badge status="info">{t('common:filter.caseLockedBadge')}</Badge>
        )}
      </FilterSection>
    </FilterToolbar>
  )
}
